/**
 * Claim stakeholders (« Intervenants » tab — fiche 22740): a `DataList` with
 * the stable columns of `./claimStakeholdersConfig.ts`, rows in API order.
 * Overrides / publish / loading / error / empty states are handled by the
 * shared `DataTableCard`.
 *
 * NOT registered by itself — registered as `extranet.claimStakeholdersCard`.
 */

import { useMemo } from 'react';
import { Users, type LucideIcon } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import type { ClaimStakeholderRow } from '../../api/claimsService';
import { DataTableCard } from '../shared/DataTableCard';
import { buildClaimStakeholdersConfig } from './claimStakeholdersConfig';

export interface ClaimStakeholdersCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row (the tab names the content). */
  title?: string;
  icon?: LucideIcon;
  rows: readonly ClaimStakeholderRow[];
  loading?: boolean;
  error?: string | null;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function ClaimStakeholdersCard({
  componentId,
  title,
  icon = Users,
  rows,
  loading = false,
  error = null,
  _layoutOverrides,
}: Readonly<ClaimStakeholdersCardProps>) {
  const config = useMemo(
    () => buildClaimStakeholdersConfig(componentId, rows),
    [componentId, rows],
  );

  return (
    <DataTableCard
      componentId={componentId}
      title={title}
      icon={icon}
      config={config}
      rowCount={rows.length}
      loading={loading}
      error={error}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
